import React, { useEffect, useState } from 'react';

import { hasRoles, EDIT_CLIENT_ROLES } from '../../../../util/RoleUtil';
import { validateEmailAddress, validatePhoneNumber } from '../../../../util/ValidationUtil';
import { validateGPS } from '../../../../util/GPSValidationUtil';

import { ClientContactDetails } from './contact-details/ClientContactDetails';
import { ClientAddressInformation } from './adresses/ClientAddressInformation';
import { ClientAvailability } from './availability/ClientAvailability';
import { ClientAdditionalInfo } from './additional-info/ClientAdditionalInfo';
import { ClientsRelationsSearch } from './relations/ClientsRelationsSearch';
import { SaveButton } from '../../../common/button/save-button/SaveButton';

import './ClientLocatorInformation.css';

export const ClientLocatorInformationTab = ({
  client,
  setClient,
  setActiveTab,
  handleUpdate,
  context,
  setContext,
  setSpinnerText,
  showToaster,
  toolTipsEnabled,
}) => {
  const [activeChildTab, setActiveChildTab] = useState('clientsContactDetails');

  const [clientContactDetails, setClientContactDetails] = useState({
    selectedSecurityQuestion: null,
    securityAnswer: '',
    mobileNumber: '',
    alternativeNumber: '',
    mobileNumberVerified: false,
    alternativeNumberVerified: false,
    selectedMobileNumberOwner: '',
    selectedAlternativeNumberOwner: '',
    emailAddress: '',
    emailAddressVerified: false,
    confidentialityInstructions: '',
    isAppointment: false,
    isStudyAppointment: false,
    isFriend: false,
    isDoctorsAppointment: false,
    isAurum: false,
    isOther: false,
    otherSpecify: '',
  });

  const [clientContactDetailsErrors, setClientContactDetailsErrors] = useState({
    securityAnswerError: '',
    securityQuestionError: '',
    mobileNumberOwnerError: '',
    alternativeNumberOwnerError: '',
    mobileNumberError: '',
    alternativeNumberError: '',
    emailError: '',
    otherSpecifyError: '',
  });

  const [clientAddressInformation, setClientAddressInformation] = useState({
    primaryAddressLine1: '',
    primaryAddressLine2: '',
    primarySuburb: '',
    primaryCity: '',
    selectedPrimaryProvince: '',
    primaryGPSCoordinates: '',
    primaryAdditionalInfo: '',
    secondaryAddressLine1: '',
    secondaryAddressLine2: '',
    secondarySuburb: '',
    secondaryCity: '',
    selectedSecondaryProvince: '',
    secondaryGPSCoordinates: '',
    secondaryAdditionalInfo: '',
  });

  const [clientAddressErrors, setClientAddressErrors] = useState({
    addressLineOneError: '',
    suburbOneError: '',
    cityOneError: '',
    provinceOneError: '',
    GPSOneError: '',
    GPSTwoError: '',
  });

  const [clientRelationInformation, setClientRelationInformation] = useState({
    searchResults: [],
    relationFirstName: '',
    relationLastName: '',
    relationship: '',
    relationMobile: '',
    relationMobileNumberVerified: false,
    selectedDiscretion: '',
    otherSpecify: '',
    relationEmail: '',
    emailVerified: false,
    isClientMinor: false,
    isLegalGuardian: false,
    isMainContact: false,
  });

  const [clientRelationError, setClientRelationError] = useState({
    firstNameError: '',
    lastNameError: '',
    relationshipError: '',
    mobileNumberError: '',
    emailError: '',
    otherSpecifyError: '',
  });

  const [clientAdditionalInformation, setClientAdditionalInformation] = useState({
    isContactableAtWork: false,
    homeVisitAllowed: false,
    companyName: '',
    clientKnownAs: '',
    telNumber: '',
    additionalInfo: '',
  });

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Any Day'];
  const [availability, setAvailability] = useState(() => {
    const initialAvailability = Object.fromEntries(
      daysOfWeek.map((day) => [
        day,
        {
          earlyMorning: false,
          morning: false,
          afternoon: false,
          lateAfternoon: false,
          anyTime: false,
        },
      ])
    );
    return initialAvailability;
  });

  useEffect(() => {
    if (client && client.locatorInformation && client.locatorInformation?.contactDetails) {
      let currentClientContactDetails = client.locatorInformation?.contactDetails;
      let whatToSayWhenWeCall = client.locatorInformation?.contactDetails.whatToSayWhenWeCall;

      setClientContactDetails({
        selectedSecurityQuestion: currentClientContactDetails.securityQuestion
          ? { value: currentClientContactDetails.securityQuestion, label: currentClientContactDetails.securityQuestion }
          : null,
        securityAnswer: currentClientContactDetails.securityAnswer || '',
        mobileNumber: currentClientContactDetails.mobileNumber || '',
        alternativeNumber: currentClientContactDetails.alternativeNumber || '',
        mobileNumberVerified: currentClientContactDetails.mobileNumberVerified || false,
        alternativeNumberVerified: currentClientContactDetails.alternativeNumberVerified || false,
        selectedMobileNumberOwner: currentClientContactDetails.whoseMobileNumber
          ? {
              value: currentClientContactDetails.whoseMobileNumber,
              label: currentClientContactDetails.whoseMobileNumber,
            }
          : '',
        selectedAlternativeNumberOwner: currentClientContactDetails.whoseAlternativeNumber
          ? {
              value: currentClientContactDetails.whoseAlternativeNumber,
              label: currentClientContactDetails.whoseAlternativeNumber,
            }
          : '',
        emailAddress: currentClientContactDetails.emailAddress || '',
        emailAddressVerified: currentClientContactDetails.emailAddressVerified || false,
        confidentialityInstructions: currentClientContactDetails.confidentialityInstructions || '',
        isAppointment: whatToSayWhenWeCall === 'Appointment',
        // eslint-disable-next-line quotes
        isDoctorsAppointment: whatToSayWhenWeCall === "Doctor's Appointment",
        isStudyAppointment: whatToSayWhenWeCall === 'Study Appointment',
        isFriend: whatToSayWhenWeCall === 'Friend',
        isAurum: whatToSayWhenWeCall === 'Aurum',
        isOther:
          whatToSayWhenWeCall !== 'Appointment' &&
          // eslint-disable-next-line quotes
          whatToSayWhenWeCall !== "Doctor's Appointment" &&
          whatToSayWhenWeCall !== 'Study Appointment' &&
          whatToSayWhenWeCall !== 'Friend' &&
          whatToSayWhenWeCall !== 'Aurum',
        otherSpecify: !(
          whatToSayWhenWeCall !== 'Appointment' &&
          // eslint-disable-next-line quotes
          whatToSayWhenWeCall !== "Doctor's Appointment" &&
          whatToSayWhenWeCall !== 'Study Appointment' &&
          whatToSayWhenWeCall !== 'Friend' &&
          whatToSayWhenWeCall !== 'Aurum'
        )
          ? ''
          : whatToSayWhenWeCall,
      });
    }
  }, [client]);

  useEffect(() => {
    setActiveTab('clientLocatorInformation');
  }, [setActiveTab]);

  useEffect(() => {
    if (client) {
      const primaryAddress = client.locatorInformation?.addresses.find((address) => address.primaryResidence === true);
      const secondaryAddress = client.locatorInformation?.addresses.find(
        (address) => address.primaryResidence === false || address.primaryResidence === null
      );

      if (primaryAddress && !secondaryAddress) {
        setClientAddressInformation((prev) => ({
          ...prev,
          primaryAddressLine1: primaryAddress.addressLineOne || '',
          primaryAddressLine2: primaryAddress.addressLineTwo || '',
          primarySuburb: primaryAddress.suburb || '',
          primaryCity: primaryAddress.city || '',
          selectedPrimaryProvince: primaryAddress.province
            ? { value: primaryAddress.province, label: primaryAddress.province }
            : null,
          primaryGPSCoordinates: primaryAddress.coordinates || '',
          primaryAdditionalInfo: primaryAddress.additionalInformation || '',
        }));
      }

      if (secondaryAddress) {
        setClientAddressInformation((prev) => ({
          ...prev,
          primaryAddressLine1: primaryAddress.addressLineOne || '',
          primaryAddressLine2: primaryAddress.addressLineTwo || '',
          primarySuburb: primaryAddress.suburb || '',
          primaryCity: primaryAddress.city || '',
          selectedPrimaryProvince: primaryAddress.province
            ? { value: primaryAddress.province, label: primaryAddress.province }
            : null,
          primaryGPSCoordinates: primaryAddress.coordinates || '',
          primaryAdditionalInfo: primaryAddress.additionalInformation || '',
          secondaryAddressLine1: secondaryAddress.addressLineOne || '',
          secondaryAddressLine2: secondaryAddress.addressLineTwo || '',
          secondarySuburb: secondaryAddress.suburb || '',
          secondaryCity: secondaryAddress.city || '',
          selectedSecondaryProvince: secondaryAddress.province
            ? { value: secondaryAddress.province, label: secondaryAddress.province }
            : null,
          secondaryGPSCoordinates: secondaryAddress.coordinates || '',
          secondaryAdditionalInfo: secondaryAddress.additionalInformation || '',
        }));
      }
    }
  }, [client]);

  useEffect(() => {
    if (client && client.dob) {
      setClientRelationInformation((prev) => ({
        ...prev,
        searchResults: client.locatorInformation?.relations,
        isClientMinor: calculateMinor(client.dob),
      }));
    } else {
      setClientRelationInformation((prev) => ({
        ...prev,
        searchResults: client.locatorInformation?.relations,
      }));
    }
  }, [client]);

  useEffect(() => {
    if (client) {
      let additionalInfo = client.locatorInformation?.additionalInformation;

      setClientAdditionalInformation({
        isContactableAtWork: additionalInfo?.contactableAtWork || false,
        homeVisitAllowed: additionalInfo?.canPerformHomeVisit || false,
        companyName: additionalInfo?.companyName || '',
        clientKnownAs: additionalInfo?.clientKnownAs || '',
        telNumber: additionalInfo?.telNumber || '',
        additionalInfo: additionalInfo?.additionalTracingInfo || '',
      });
    }
  }, [client]);

  useEffect(() => {
    if (client && client.locatorInformation && client.locatorInformation?.availability) {
      const convertedAvailability = convertAvailabilityKeysToDesiredCase(client.locatorInformation?.availability);
      setAvailability(convertedAvailability);
    }
  }, [client]);

  const getExternalReference = (client, isPrimaryResidence) => {
    const address = client.locatorInformation?.addresses.find(
      (addr) =>
        addr.primaryResidence === isPrimaryResidence || (isPrimaryResidence === false && addr.primaryResidence === null)
    );
    return address ? address.externalReference : null;
  };

  const calculateMinor = (birthdate) => {
    const today = new Date();
    const birthDate = new Date(birthdate);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age < 18;
  };

  const convertAvailabilityKeysToDesiredCase = (availability) => {
    const convertedAvailability = {};

    Object.keys(availability).forEach((day) => {
      const convertedDay = day.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());
      convertedAvailability[convertedDay] = availability[day];
    });

    return convertedAvailability;
  };

  const convertAvailabilityKeysToLowerCase = (availability) => {
    const convertedAvailability = {};

    Object.keys(availability).forEach((day) => {
      const lowercaseDay = day.toLowerCase();
      const camelCaseDay = lowercaseDay === 'any day' ? 'anyDay' : lowercaseDay;
      convertedAvailability[camelCaseDay] = availability[day];
    });

    return convertedAvailability;
  };

  const handleClientContactDetailsChange = (key, value) => {
    if (key === 'isAppointment') {
      setClientContactDetails({
        ...clientContactDetails,
        isAppointment: true,
        isStudyAppointment: false,
        isFriend: false,
        isDoctorsAppointment: false,
        isAurum: false,
        isOther: false,
        otherSpecify: '',
      });
    } else if (key === 'isStudyAppointment') {
      setClientContactDetails({
        ...clientContactDetails,
        isAppointment: false,
        isStudyAppointment: true,
        isFriend: false,
        isDoctorsAppointment: false,
        isAurum: false,
        isOther: false,
        otherSpecify: '',
      });
    } else if (key === 'isFriend') {
      setClientContactDetails({
        ...clientContactDetails,
        isAppointment: false,
        isStudyAppointment: false,
        isFriend: true,
        isDoctorsAppointment: false,
        isAurum: false,
        isOther: false,
        otherSpecify: '',
      });
    } else if (key === 'isDoctorsAppointment') {
      setClientContactDetails({
        ...clientContactDetails,
        isAppointment: false,
        isStudyAppointment: false,
        isFriend: false,
        isDoctorsAppointment: true,
        isAurum: false,
        isOther: false,
        otherSpecify: '',
      });
    } else if (key === 'isAurum') {
      setClientContactDetails({
        ...clientContactDetails,
        isAppointment: false,
        isStudyAppointment: false,
        isFriend: false,
        isDoctorsAppointment: false,
        isAurum: true,
        isOther: false,
        otherSpecify: '',
      });
    } else if (key === 'isOther') {
      setClientContactDetails({
        ...clientContactDetails,
        isAppointment: false,
        isStudyAppointment: false,
        isFriend: false,
        isDoctorsAppointment: false,
        isAurum: false,
        isOther: true,
      });
    } else {
      setClientContactDetails({ ...clientContactDetails, [key]: value });
    }
  };

  const handleClientAddressInformationChange = (key, value) => {
    setClientAddressInformation({ ...clientAddressInformation, [key]: value });
  };

  const handleClientRelationInformationChange = (key, value) => {
    setClientRelationInformation({ ...clientRelationInformation, [key]: value });
  };

  const handleClientRelationErrors = (key, value) => {
    setClientRelationError({ ...clientRelationError, [key]: value });
  };

  const handleClientAdditionalInformationChange = (key, value) => {
    if (key === 'isContactableAtWork' && !value) {
      setClientAdditionalInformation({
        ...clientAdditionalInformation,
        isContactableAtWork: value,
        companyName: '',
        clientKnownAs: '',
        telNumber: '',
      });
    } else {
      setClientAdditionalInformation({ ...clientAdditionalInformation, [key]: value });
    }
  };

  const handleSaveAllClick = () => {
    const contactDetails = validateClientDetails();
    const addresses = validateAddress();
    const relations = validateRelations();
    const additionalInformation = validateAdditionalInfo();
    const availability = validateAvailability();

    if (!addresses || addresses.length === 0) {
      showToaster({
        responseStatus: 'Error',
        responseMessage: 'Please complete address section.',
      });
      return;
    }

    if (contactDetails) {
      const newClient = {
        ...client,
        locatorInformation: {
          contactDetails: contactDetails,
          addresses: addresses,
          relations: relations,
          additionalInformation: additionalInformation,
          availability: availability,
        },
      };
      handleUpdate(newClient);
    }
  };

  const validateClientDetails = () => {
    let whatToSayWhenWeCall;
    if (clientContactDetails.isAurum) {
      whatToSayWhenWeCall = 'Aurum';
    }

    if (clientContactDetails.isAppointment) {
      whatToSayWhenWeCall = 'Appointment';
    }

    if (clientContactDetails.isDoctorsAppointment) {
      // eslint-disable-next-line quotes
      whatToSayWhenWeCall = "Doctor's Appointment";
    }

    if (clientContactDetails.isStudyAppointment) {
      whatToSayWhenWeCall = 'Study Appointment';
    }

    if (clientContactDetails.isFriend) {
      whatToSayWhenWeCall = 'Friend';
    }

    if (clientContactDetails.isOther || clientContactDetails.otherSpecify) {
      whatToSayWhenWeCall = clientContactDetails.otherSpecify;
    }

    const errors = {};
    if (clientContactDetails.securityAnswer && !clientContactDetails.selectedSecurityQuestion) {
      errors.securityQuestionError = '*REQUIRED';
    } else {
      errors.securityQuestionError = '';
    }

    if (clientContactDetails.selectedSecurityQuestion && !clientContactDetails.securityAnswer) {
      errors.securityAnswerError = '*REQUIRED';
    } else {
      errors.securityAnswerError = '';
    }

    if (clientContactDetails.mobileNumber && !validatePhoneNumber(clientContactDetails.mobileNumber)) {
      errors.mobileNumberError = 'Number must be 10 digits';
    } else {
      errors.mobileNumberError = '';
    }

    if (clientContactDetails.alternativeNumber && !validatePhoneNumber(clientContactDetails.alternativeNumber)) {
      errors.alternativeNumberError = 'Number must be 10 digits';
    } else {
      errors.alternativeNumberError = '';
    }

    if (clientContactDetails.mobileNumber && !clientContactDetails.selectedMobileNumberOwner) {
      errors.mobileNumberOwnerError = '*REQUIRED';
    } else {
      errors.mobileNumberOwnerError = '';
    }

    if (clientContactDetails.alternativeNumber && !clientContactDetails.selectedAlternativeNumberOwner) {
      errors.alternativeNumberOwnerError = '*REQUIRED';
    } else {
      errors.alternativeNumberOwnerError = '';
    }

    if (clientContactDetails.emailAddress && !validateEmailAddress(clientContactDetails.emailAddress)) {
      errors.emailError = 'Invalid email address';
    } else {
      errors.emailError = '';
    }

    if (clientContactDetails.isOther && !clientContactDetails.otherSpecify) {
      errors.otherSpecifyError = '*REQUIRED';
    } else {
      errors.otherSpecifyError = '';
    }

    setClientContactDetailsErrors(errors);

    for (let key in errors) {
      if (errors[key] !== '') return;
    }

    return {
      securityQuestion: clientContactDetails.selectedSecurityQuestion
        ? clientContactDetails.selectedSecurityQuestion.label
        : null,
      securityAnswer: clientContactDetails.securityAnswer ? clientContactDetails.securityAnswer : '',
      mobileNumber: clientContactDetails.mobileNumber ? clientContactDetails.mobileNumber : '',
      mobileNumberVerified: clientContactDetails.mobileNumberVerified
        ? clientContactDetails.mobileNumberVerified
        : false,
      whoseMobileNumber: clientContactDetails.selectedMobileNumberOwner
        ? clientContactDetails.selectedMobileNumberOwner.label
        : null,
      alternativeNumber: clientContactDetails.alternativeNumber ? clientContactDetails.alternativeNumber : '',
      alternativeNumberVerified: clientContactDetails.alternativeNumberVerified
        ? clientContactDetails.alternativeNumberVerified
        : false,
      whoseAlternativeNumber: clientContactDetails.selectedAlternativeNumberOwner
        ? clientContactDetails.selectedAlternativeNumberOwner.label
        : null,
      emailAddress: clientContactDetails.emailAddress ? clientContactDetails.emailAddress : '',
      emailAddressVerified: clientContactDetails.emailAddressVerified
        ? clientContactDetails.emailAddressVerified
        : false,
      confidentialityInstructions: clientContactDetails.confidentialityInstructions
        ? clientContactDetails.confidentialityInstructions
        : '',
      whatToSayWhenWeCall: whatToSayWhenWeCall,
    };
  };

  const validateAddress = () => {
    let validPrimaryGPS = true;
    let validSecondaryGPS = true;

    if (clientAddressInformation.primaryGPSCoordinates) {
      validPrimaryGPS = validateGPS(clientAddressInformation.primaryGPSCoordinates);
    }

    if (clientAddressInformation.secondaryGPSCoordinates) {
      validSecondaryGPS = validateGPS(clientAddressInformation.secondaryGPSCoordinates);
    }

    setClientAddressErrors({
      addressLineOneError: !clientAddressInformation.primaryAddressLine1 ? '*REQUIRED' : '',
      suburbOneError: !clientAddressInformation.primarySuburb ? '*REQUIRED' : '',
      cityOneError: !clientAddressInformation.primaryCity ? '*REQUIRED' : '',
      provinceOneError: !clientAddressInformation.selectedPrimaryProvince ? '*REQUIRED' : '',
      GPSOneError: !validPrimaryGPS ? '*Not a Valid GPS Co-Ordinate Format' : '',
      GPSTwoError: !validSecondaryGPS ? '*Not a Valid GPS Co-Ordinate Format' : '',
    });

    if (
      !clientAddressInformation.primaryAddressLine1 ||
      !clientAddressInformation.primarySuburb ||
      !clientAddressInformation.primaryCity ||
      !clientAddressInformation.selectedPrimaryProvince ||
      !validPrimaryGPS ||
      !validSecondaryGPS
    ) {
      return [];
    }

    const updatedAddresses = [
      {
        primaryResidence: true,
        addressLineOne: clientAddressInformation.primaryAddressLine1,
        addressLineTwo: clientAddressInformation.primaryAddressLine2,
        city: clientAddressInformation.primaryCity,
        coordinates: clientAddressInformation.primaryGPSCoordinates,
        province: clientAddressInformation.selectedPrimaryProvince
          ? clientAddressInformation.selectedPrimaryProvince.label
          : null,
        suburb: clientAddressInformation.primarySuburb,
        additionalInformation: clientAddressInformation.primaryAdditionalInfo,
        externalReference: getExternalReference(client, true),
      },
      {
        primaryResidence: false,
        addressLineOne: clientAddressInformation.secondaryAddressLine1,
        addressLineTwo: clientAddressInformation.secondaryAddressLine2,
        city: clientAddressInformation.secondaryCity,
        coordinates: clientAddressInformation.secondaryGPSCoordinates,
        province: clientAddressInformation.selectedSecondaryProvince
          ? clientAddressInformation.selectedSecondaryProvince.label
          : null,
        suburb: clientAddressInformation.secondarySuburb,
        additionalInformation: clientAddressInformation.secondaryAdditionalInfo,
        externalReference: getExternalReference(client, false),
      },
    ];

    return updatedAddresses;
  };

  const validateRelations = () => {
    return clientRelationInformation.searchResults;
  };

  const validateAdditionalInfo = () => {
    return {
      additionalTracingInfo: clientAdditionalInformation.additionalInfo || '',
      clientKnownAs: clientAdditionalInformation.clientKnownAs || '',
      companyName: clientAdditionalInformation.companyName || '',
      contactableAtWork: clientAdditionalInformation.isContactableAtWork || false,
      canPerformHomeVisit: clientAdditionalInformation.homeVisitAllowed || false,
      telNumber: clientAdditionalInformation.telNumber || '',
    };
  };

  const validateAvailability = () => {
    const defaultAvailability = {
      earlyMorning: false,
      morning: false,
      afternoon: false,
      lateAfternoon: false,
      anyTime: false,
    };

    const updatedAvailability = Object.fromEntries(daysOfWeek.map((day) => [day, { ...defaultAvailability }]));

    daysOfWeek.forEach((day) => {
      Object.keys(updatedAvailability[day]).forEach((time) => {
        updatedAvailability[day][time] = availability[day][time];
      });
    });

    const convertedAvailability = convertAvailabilityKeysToLowerCase(updatedAvailability);

    return convertedAvailability;
  };

  const fieldsDisabled = !hasRoles(context, EDIT_CLIENT_ROLES);

  const screenComponents = {
    clientsContactDetails: (
      <ClientContactDetails
        context={context}
        setContext={setContext}
        toolTipsEnabled={toolTipsEnabled}
        showToaster={showToaster}
        setSpinnerText={setSpinnerText}
        clientContactDetails={clientContactDetails}
        clientContactDetailsErrors={clientContactDetailsErrors}
        handleClientContactDetailsChange={handleClientContactDetailsChange}
        clientRelationInformation={clientRelationInformation}
        setClientRelationInformation={setClientRelationInformation}
        clientRelationError={clientRelationError}
        setClientRelationError={setClientRelationError}
        handleClientRelationInformationChange={handleClientRelationInformationChange}
        handleClientRelationErrors={handleClientRelationErrors}
        fieldsDisabled={fieldsDisabled}
      />
    ),
    clientsAddresses: (
      <ClientAddressInformation
        context={context}
        setContext={setContext}
        toolTipsEnabled={toolTipsEnabled}
        showToaster={showToaster}
        setSpinnerText={setSpinnerText}
        clientAddressInformation={clientAddressInformation}
        clientAddressErrors={clientAddressErrors}
        handleClientAddressInformationChange={handleClientAddressInformationChange}
        fieldsDisabled={fieldsDisabled}
      />
    ),
    clientsRelations: (
      <ClientsRelationsSearch
        context={context}
        setContext={setContext}
        toolTipsEnabled={toolTipsEnabled}
        showToaster={showToaster}
        setSpinnerText={setSpinnerText}
        clientRelationInformation={clientRelationInformation}
        setClientRelationInformation={setClientRelationInformation}
        clientRelationError={clientRelationError}
        setClientRelationError={setClientRelationError}
        handleClientRelationInformationChange={handleClientRelationInformationChange}
        handleClientRelationErrors={handleClientRelationErrors}
        setClient={setClient}
        fieldsDisabled={fieldsDisabled}
      />
    ),
    clientsAvailability: (
      <ClientAvailability
        context={context}
        setContext={setContext}
        toolTipsEnabled={toolTipsEnabled}
        showToaster={showToaster}
        setSpinnerText={setSpinnerText}
        availability={availability}
        setAvailability={setAvailability}
        fieldsDisabled={fieldsDisabled}
      />
    ),
    clientsAdditionalInfo: (
      <ClientAdditionalInfo
        context={context}
        setContext={setContext}
        toolTipsEnabled={toolTipsEnabled}
        showToaster={showToaster}
        setSpinnerText={setSpinnerText}
        clientAdditionalInformation={clientAdditionalInformation}
        handleClientAdditionalInformationChange={handleClientAdditionalInformationChange}
        fieldsDisabled={fieldsDisabled}
      />
    ),
  };

  const handleNavigationClick = (tab) => {
    if (activeChildTab === 'clientsContactDetails') {
      if (clientContactDetails.selectedSecurityQuestion && !clientContactDetails.securityAnswer) {
        setClientContactDetailsErrors({
          ...clientContactDetailsErrors,
          securityAnswerError: '*REQUIRED',
        });
        return;
      } else {
        setClientContactDetailsErrors({
          ...clientContactDetailsErrors,
          securityAnswerError: '',
        });
      }

      if (clientContactDetails.securityAnswer && !clientContactDetails.selectedSecurityQuestion) {
        setClientContactDetailsErrors({
          ...clientContactDetailsErrors,
          securityQuestionError: '*REQUIRED',
        });
        return;
      } else {
        setClientContactDetailsErrors({
          ...clientContactDetailsErrors,
          securityQuestionError: '"',
        });
      }

      if (clientContactDetails.mobileNumber) {
        if (!validatePhoneNumber(clientContactDetails.mobileNumber)) {
          setClientContactDetailsErrors({
            ...clientContactDetailsErrors,
            mobileNumberError: 'Number must be 10 digits',
          });
          return;
        } else {
          setClientContactDetailsErrors({
            ...clientContactDetailsErrors,
            mobileNumberError: '',
          });
        }

        if (clientContactDetails.mobileNumber && !clientContactDetails.selectedMobileNumberOwner) {
          setClientContactDetailsErrors({
            ...clientContactDetailsErrors,
            mobileNumberOwnerError: '*REQUIRED',
          });
          return;
        } else {
          setClientContactDetailsErrors({
            ...clientContactDetailsErrors,
            mobileNumberOwnerError: '',
          });
        }
      }

      if (clientContactDetails.alternativeNumber) {
        if (!validatePhoneNumber(clientContactDetails.alternativeNumber)) {
          setClientContactDetailsErrors({
            ...clientContactDetailsErrors,
            alternativeNumberError: 'Number must be 10 digits',
          });
          return;
        } else {
          setClientContactDetailsErrors({
            ...clientContactDetailsErrors,
            alternativeNumberError: '',
          });
        }

        if (clientContactDetails.alternativeNumber && !clientContactDetails.selectedAlternativeNumberOwner) {
          setClientContactDetailsErrors({
            ...clientContactDetailsErrors,
            alternativeNumberOwnerError: '*REQUIRED',
          });
          return;
        } else {
          setClientContactDetailsErrors({
            ...clientContactDetailsErrors,
            alternativeNumberOwnerError: '',
          });
        }
      }

      if (clientContactDetails.emailAddress) {
        if (!validateEmailAddress(clientContactDetails.emailAddress)) {
          setClientContactDetailsErrors({
            ...clientContactDetailsErrors,
            emailError: 'Invalid email address',
          });
          return;
        } else {
          setClientContactDetailsErrors({
            ...clientContactDetailsErrors,
            emailError: '',
          });
        }
      }

      if (clientContactDetails.isOther && clientContactDetails.otherSpecify === '') {
        setClientContactDetailsErrors({
          ...clientContactDetailsErrors,
          otherSpecifyError: '*REQUIRED',
        });
        return;
      } else {
        setClientContactDetailsErrors({
          ...clientContactDetailsErrors,
          otherSpecifyError: '',
        });
      }
    } else if (activeChildTab === 'clientsAddresses') {
      if (
        clientAddressInformation.primaryAddressLine1 ||
        clientAddressInformation.primarySuburb ||
        clientAddressInformation.primaryCity ||
        clientAddressInformation.selectedPrimaryProvince
      ) {
        setClientAddressErrors({
          addressLineOneError: !clientAddressInformation.primaryAddressLine1 ? '*REQUIRED' : '',
          suburbOneError: !clientAddressInformation.primarySuburb ? '*REQUIRED' : '',
          cityOneError: !clientAddressInformation.primaryCity ? '*REQUIRED' : '',
          provinceOneError: !clientAddressInformation.selectedPrimaryProvince ? '*REQUIRED' : '',
        });

        if (
          !clientAddressInformation.primaryAddressLine1 ||
          !clientAddressInformation.primarySuburb ||
          !clientAddressInformation.primaryCity ||
          !clientAddressInformation.selectedPrimaryProvince
        ) {
          return;
        }
      }
    }
    setActiveChildTab(tab);
  };

  return (
    <div className="locator-information-container">
      <div className="locator-information-inner-container">
        <div className="locator-information-tab-container">
          {Object.keys(screenComponents).map((tab) => (
            <div
              key={tab}
              className={`locator-information-tab ${activeChildTab === tab ? 'active-tab' : ''}`}
              onClick={() => handleNavigationClick(tab)}
            >
              {tab === 'clientsContactDetails' && 'Contact Details'}
              {tab === 'clientsAddresses' && 'Addresses'}
              {tab === 'clientsRelations' && 'Relations'}
              {tab === 'clientsAvailability' && 'Availability'}
              {tab === 'clientsAdditionalInfo' && 'Additional Info'}
            </div>
          ))}
        </div>
        <div className={`locator-information-content ${activeChildTab}`}>{screenComponents[activeChildTab]}</div>
      </div>

      <div className={'client-locator-button-container'}>
        <SaveButton color="#dca933" title="Save Changes" disabled={fieldsDisabled} onClick={handleSaveAllClick} />
      </div>
    </div>
  );
};
